/*
 * jx-cart-ui.js — Jahmaxx quick-add stepper + sticky cart bar.
 *
 * Two jobs, one file because they share cart state:
 *
 *  1. Every single-variant quick-add form on a product card gets a "+" button.
 *     Tapping it adds one to the cart and the "+" becomes the live quantity
 *     (1, 2, 3 ...). A "-" slides in so the shopper can back out. The cart
 *     drawer is never opened, because we never submit Xtra's form — we talk to
 *     the Cart AJAX API ourselves.
 *
 *  2. A fixed bar at the bottom of the viewport shows total items, subtotal and
 *     a progress bar toward the spend goal configured in snippets/jx-cart-bar.liquid.
 *
 * Nothing here forks an Xtra file. If Xtra's markup changes, the worst case is
 * that decorate() finds no forms and the native quick-add is left alone.
 */
(function () {
  'use strict';

  var bar = document.querySelector('[data-jx-cart-bar]');
  if (!bar) return;

  // ---------------------------------------------------------------- config --
  var GOAL_CENTS = Number(bar.dataset.goalCents) || 0;
  var GOAL_MODE = bar.dataset.goalMode === 'limit' ? 'limit' : 'reward';
  var MONEY_FORMAT = bar.dataset.moneyFormat || '${{amount}}';

  // Every string is a theme-editor setting on the section, so merch can reword
  // the bar without touching code. {placeholders} are filled by t().
  var T = {
    item: bar.dataset.tItem || 'item',
    items: bar.dataset.tItems || 'items',
    remaining: bar.dataset.tRemaining || '{amount} to {goal}',
    reached: bar.dataset.tReached || "You've reached {goal}",
    weight: bar.dataset.tWeight || '{lbs} of {limit} lbs',
    weightOver: bar.dataset.tWeightOver || 'Over the {limit} lb limit ({lbs} lbs)'
  };

  function t(key, replacements) {
    return String(T[key]).replace(/\{(\w+)\}/g, function (match, name) {
      return replacements && replacements[name] != null ? replacements[name] : match;
    });
  }

  var GRAMS_PER_POUND = 453.59237;
  var SHOW_WEIGHT = bar.dataset.showWeight !== 'false';
  var WEIGHT_LIMIT_LBS = Number(bar.dataset.weightLimit) || 40;
  var WEIGHT_WARN_LBS = Number(bar.dataset.weightWarnAt) || 30;

  var COMMIT_DELAY = 350; // ms of quiet before a run of taps is sent to Shopify

  function route(name, fallback) {
    var url = (window.routes && window.routes[name]) || fallback;
    return String(url).replace(/\.js$/, '') + '.js';
  }
  var CART_JSON = route('cart_url', '/cart');
  var ADD_JSON = route('cart_add_url', '/cart/add');
  var CHANGE_JSON = route('cart_change_url', '/cart/change');

  // ----------------------------------------------------------------- money --
  function formatMoney(cents) {
    var value = Math.round(Number(cents) || 0);

    function withDelimiters(number, precision, thousands, decimal) {
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, name) {
      switch (name) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  // ------------------------------------------------------------ cart state --
  var cart = null; // last confirmed /cart.js payload
  var actual = new Map(); // variantId -> quantity Shopify has confirmed
  var target = new Map(); // variantId -> quantity the shopper has tapped to
  var pending = new Map(); // variantId -> true while a request is in flight
  var priceCache = new Map();
  var commitTimer = null;
  var flushing = false;

  function quantitiesFrom(payload) {
    var map = new Map();
    (payload.items || []).forEach(function (item) {
      var id = String(item.variant_id);
      map.set(id, (map.get(id) || 0) + item.quantity);
    });
    return map;
  }

  function displayed(id) {
    return target.has(id) ? target.get(id) : actual.get(id) || 0;
  }

  function priceOf(id) {
    if (priceCache.has(id)) return priceCache.get(id);
    var node = document.querySelector('[data-jx-qa][data-variant-id="' + id + '"]');
    var price = node ? Number(node.dataset.price) : 0;
    if (price) priceCache.set(id, price);
    return price;
  }

  // Subtotal and count while requests are still in flight: start from the
  // confirmed cart, then apply the deltas the shopper has already tapped.
  function optimisticTotals() {
    var count = cart ? cart.item_count : 0;
    var subtotal = 0;
    if (cart) {
      subtotal = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
    }

    target.forEach(function (wanted, id) {
      var have = actual.get(id) || 0;
      var delta = wanted - have;
      if (!delta) return;
      count += delta;
      subtotal += delta * (priceOf(id) || 0);
    });

    return { count: Math.max(0, count), subtotal: Math.max(0, subtotal) };
  }

  function adopt(payload) {
    cart = payload;
    actual = quantitiesFrom(payload);
    (payload.items || []).forEach(function (item) {
      priceCache.set(String(item.variant_id), item.final_price != null ? item.final_price : item.price);
    });
    target.forEach(function (wanted, id) {
      if (!pending.get(id) && wanted === (actual.get(id) || 0)) target.delete(id);
    });
    render();
    syncThemeChrome(payload);
  }

  function loadCart() {
    return fetch(CART_JSON, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart fetch failed');
        return response.json();
      })
      .then(function (payload) {
        adopt(payload);
        return payload;
      });
  }

  function commit(id, wanted) {
    var have = actual.get(id) || 0;
    var creating = have === 0 && wanted > 0;
    var url = creating ? ADD_JSON : CHANGE_JSON;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: Math.max(0, wanted) })
    })
      .then(function (response) {
        return response.json().then(function (payload) {
          if (!response.ok) {
            throw new Error(payload.description || payload.message || 'Could not update the cart.');
          }
          return payload;
        });
      })
      .then(function (payload) {
        // /cart/add.js returns the line item; /cart/change.js returns the cart.
        var settled = payload.items ? Promise.resolve(adopt(payload)) : loadCart();
        return settled.then(function (result) {
          // jahmaxx-theme-enhancements.js used to re-apply the DIN / inmate cart
          // attributes on quick-add *submit*. We never submit, so tell it here.
          window.dispatchEvent(new CustomEvent('jx:cart-updated'));
          return result;
        });
      });
  }

  function flush() {
    if (flushing) return Promise.resolve();
    flushing = true;

    function next() {
      var entry = target.entries().next().value;
      if (!entry) return Promise.resolve();

      var id = entry[0];
      var wanted = entry[1];
      if (wanted === (actual.get(id) || 0)) {
        target.delete(id);
        return next();
      }

      pending.set(id, true);
      render();

      return commit(id, wanted)
        .then(function () {
          if (target.get(id) === wanted) target.delete(id);
        })
        .catch(function (error) {
          target.delete(id);
          announce(error.message || 'Could not update the cart.');
          return loadCart().catch(function () {});
        })
        .then(function () {
          pending.delete(id);
          return next();
        });
    }

    return next().then(function () {
      flushing = false;
      render();
    });
  }

  function scheduleFlush() {
    window.clearTimeout(commitTimer);
    commitTimer = window.setTimeout(flush, COMMIT_DELAY);
  }

  function want(id, quantity) {
    target.set(id, Math.max(0, quantity));
    render();
    scheduleFlush();
  }

  // --------------------------------------------- keep Xtra's chrome in sync --
  function syncThemeChrome(payload) {
    var count = document.getElementById('cart-count');
    var total = document.getElementById('cart-total');
    if (count) count.innerHTML = payload.item_count;
    if (total) total.innerHTML = formatMoney(payload.total_price);

    // Refresh the side-cart panel's contents *without* opening it, so the
    // drawer is already correct whenever the shopper chooses to open it.
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      try {
        window.ajaxCart.load(false, false, false, true);
      } catch (error) {
        /* non-fatal */
      }
    }
  }

  // ----------------------------------------------------- quick-add controls --
  var CONTROL =
    '<button class="jx-qa__minus" type="button" data-jx-minus aria-label="Remove one">' +
    '<span aria-hidden="true"></span></button>' +
    '<button class="jx-qa__main" type="button" data-jx-plus>' +
    '<span class="jx-qa__glyph" aria-hidden="true"></span>' +
    '<span class="jx-qa__num" data-jx-num aria-hidden="true"></span>' +
    '<span class="jx-qa__sr" data-jx-label>Add to cart</span>' +
    '</button>';

  function parseCents(text) {
    var match = String(text || '').replace(/[^\d.,]/g, '').match(/\d[\d.,]*/);
    if (!match) return 0;
    var normalised = match[0].replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
    return Math.round(parseFloat(normalised) * 100) || 0;
  }

  // Xtra renders the quick-add form twice per card — once inside <figure> (the
  // desktop hover overlay) and once under the price (the mobile one). We want a
  // single control pinned to the top-right of the product image, so we mount one
  // per *card* and hide both native forms.
  function decorate() {
    var forms = document.querySelectorAll('form.form-card:not([data-jx-qa-done])');

    Array.prototype.forEach.call(forms, function (form) {
      var idInput = form.querySelector('input[name="id"][value]');
      var submit = form.querySelector('button[type="submit"]');
      if (!idInput || !submit) return; // variant picker / "View options" card — leave alone

      form.setAttribute('data-jx-qa-done', '');
      form.classList.add('jx-qa-form');

      var card = form.closest('.product-card') || form.parentElement;
      if (!card || card.querySelector('[data-jx-qa]')) return; // already has its control

      // Cards with an image get the overlay. The image-less layouts (hotspot,
      // list-compact) have no <figure>, so the control stays in the form's own
      // submit row and that one form is kept visible.
      var figure = card.querySelector('figure');
      var mount = figure || form.querySelector('p.submit') || form;
      card.classList.add(figure ? 'jx-qa-card' : 'jx-qa-card--inline');
      if (!figure) form.classList.add('jx-qa-form--host');

      var qtyInput = form.querySelector('input[name="quantity"]');
      var priceNode = card.querySelector('.price ins, .price, .money');

      var control = document.createElement('div');
      control.className = 'jx-qa';
      control.setAttribute('data-jx-qa', '');
      control.dataset.variantId = String(idInput.value);
      control.dataset.min = String(Number(qtyInput && qtyInput.min) || 1);
      control.dataset.step = String(Number(qtyInput && qtyInput.step) || 1);
      if (qtyInput && qtyInput.max) control.dataset.max = String(qtyInput.max);
      if (priceNode) {
        var cents = parseCents(priceNode.textContent);
        if (cents) control.dataset.price = String(cents);
      }
      control.innerHTML = CONTROL;
      mount.appendChild(control);

      control.querySelector('[data-jx-plus]').addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        step(control, 1);
      });
      control.querySelector('[data-jx-minus]').addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        step(control, -1);
      });
    });

    render();
  }

  function step(control, direction) {
    var id = control.dataset.variantId;
    var min = Number(control.dataset.min) || 1;
    var stepBy = Number(control.dataset.step) || 1;
    var max = control.dataset.max ? Number(control.dataset.max) : Infinity;

    var current = displayed(id);
    var next;

    if (direction > 0) next = current === 0 ? min : current + stepBy;
    else next = current <= min ? 0 : current - stepBy;

    if (next > max) {
      announce('Only ' + max + ' available.');
      control.classList.add('is-capped');
      window.setTimeout(function () {
        control.classList.remove('is-capped');
      }, 600);
      return;
    }

    control.classList.remove('is-bumped');
    void control.offsetWidth; // restart the pop animation on a repeat tap
    control.classList.add('is-bumped');

    want(id, next);
  }

  // ----------------------------------------------------------------- render --
  function render() {
    document.querySelectorAll('[data-jx-qa]').forEach(function (control) {
      var id = control.dataset.variantId;
      var quantity = displayed(id);

      control.classList.toggle('is-active', quantity > 0);
      control.classList.toggle('is-busy', Boolean(pending.get(id)));

      var num = control.querySelector('[data-jx-num]');
      if (num) num.textContent = quantity > 0 ? quantity : '';

      var label = control.querySelector('[data-jx-label]');
      if (label) {
        label.textContent = quantity > 0 ? quantity + ' in cart, add another' : 'Add to cart';
      }
    });

    renderBar();
  }

  function renderBar() {
    var totals = optimisticTotals();
    var hasItems = totals.count > 0;

    bar.hidden = !hasItems;
    document.body.classList.toggle('jx-cart-bar-open', hasItems);
    if (!hasItems) {
      document.body.style.removeProperty('--jx-cart-bar-height');
      return;
    }

    var countNode = bar.querySelector('[data-jx-count]');
    if (countNode) {
      countNode.textContent = totals.count + ' ' + (totals.count === 1 ? T.item : T.items);
    }

    var subtotalNode = bar.querySelector('[data-jx-subtotal]');
    if (subtotalNode) subtotalNode.textContent = formatMoney(totals.subtotal);

    renderGoal(totals.subtotal);
    renderWeight();

    // Centred text is balanced against the button by a spacer of equal width
    // (see the [data-align="center"] rule), so the button has to be measured.
    if (bar.dataset.align === 'center') {
      var cta = bar.querySelector('.jx-cart-bar__cta');
      if (cta) bar.style.setProperty('--jx-cta-width', cta.offsetWidth + 'px');
    }

    document.body.style.setProperty('--jx-cart-bar-height', bar.offsetHeight + 'px');
  }

  function renderGoal(subtotal) {
    var fill = bar.querySelector('[data-jx-fill]');
    var msg = bar.querySelector('[data-jx-msg]');
    var track = bar.querySelector('.jx-cart-bar__track');

    if (!GOAL_CENTS) {
      if (track) track.hidden = true;
      if (msg) msg.textContent = '';
      return;
    }

    var percent = Math.min(100, (subtotal / GOAL_CENTS) * 100);
    var remaining = GOAL_CENTS - subtotal;
    var reached = remaining <= 0;

    if (fill) fill.style.width = percent.toFixed(1) + '%';
    bar.classList.toggle('is-goal-reached', reached);
    bar.classList.toggle('is-goal-limit', reached && GOAL_MODE === 'limit');

    if (!msg) return;
    var goal = formatMoney(GOAL_CENTS);
    if (reached) {
      msg.textContent = t('reached', { goal: goal });
    } else {
      msg.textContent = t('remaining', { amount: formatMoney(remaining), goal: goal });
    }
  }

  function renderWeight() {
    var node = bar.querySelector('[data-jx-weight]');
    var separator = bar.querySelector('[data-jx-meta-sep]');
    if (!node) return;

    if (!SHOW_WEIGHT || !cart) {
      node.hidden = true;
      if (separator) separator.hidden = true;
      bar.classList.remove('is-heavy', 'is-overweight');
      return;
    }

    var pounds = (Number(cart.total_weight) || 0) / GRAMS_PER_POUND;
    var atLimit = pounds >= WEIGHT_LIMIT_LBS;
    var rounded = pounds.toFixed(1);

    node.hidden = false;
    if (separator) {
      // The dot only earns its place when there is a goal message to its left.
      var msg = bar.querySelector('[data-jx-msg]');
      separator.hidden = !msg || !msg.textContent;
    }

    bar.classList.toggle('is-heavy', pounds >= WEIGHT_WARN_LBS && !atLimit);
    bar.classList.toggle('is-overweight', atLimit);

    node.textContent = t(atLimit ? 'weightOver' : 'weight', {
      lbs: rounded,
      limit: WEIGHT_LIMIT_LBS
    });
  }

  function announce(message) {
    var live = bar.querySelector('[data-jx-live]');
    if (live) live.textContent = message;
  }

  // ------------------------------------------------------------------- boot --
  decorate();
  loadCart().catch(function () {});

  var rescan = null;
  new MutationObserver(function () {
    window.clearTimeout(rescan);
    rescan = window.setTimeout(decorate, 100);
  }).observe(document.body, { childList: true, subtree: true });

  window.addEventListener('resize', renderBar);
  window.addEventListener('ajaxCart', function () {
    loadCart().catch(function () {});
  });
  window.addEventListener('themeCartOpened', function () {
    loadCart().catch(function () {});
  });
  document.addEventListener('shopify:section:load', function () {
    decorate();
    loadCart().catch(function () {});
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) loadCart().catch(function () {});
  });

  window.jxCartUI = { refresh: loadCart, decorate: decorate };
})();
